/**
 * Utility functions for authentication-related operations
 */
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { AwsCredentialIdentityProvider } from '@aws-sdk/types';
import { AuthUser, fetchAuthSession } from 'aws-amplify/auth';
import { LoggedInUserInfo } from 'src/types/auth/auth.types';
import ENVIRONMENT_VARIABLES from '../constants/environmentVariables';

/**
 * Interface representing user access flags for admin functionality
 * Now uses Redux permissions as single source of truth
 */
export interface UserAccessFlags {
  isInitiallyDeveloper: boolean;
}

/**
 * Cache for user info extraction to avoid repeated processing
 */
interface UserInfoCache {
  userAlias: string;
  tokenHash: string;
  userInfo: LoggedInUserInfo;
  timestamp: number;
}

let userInfoCache: UserInfoCache | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Generate a simple hash for token payload to detect changes
 */
const generateTokenHash = (payload: Record<string, unknown>): string => {
  const relevantFields = [
    'custom:DISPLAY_NAME',
    'custom:GIVEN_NAME',
    'custom:FAMILY_NAME',
    'custom:EMPLOYEE_ID',
    'custom:AMZN_DEPT_NAME',
    'custom:AMZN_JOB_CODE',
    'custom:DESCRIPTION',
    'custom:LDAP_GROUPS',
    'name',
    'given_name',
    'family_name',
    'identities',
  ];

  const relevantData = relevantFields.reduce(
    (acc, field) => {
      if (payload[field] !== undefined) {
        acc[field] = payload[field];
      }
      return acc;
    },
    {} as Record<string, unknown>
  );

  return JSON.stringify(relevantData);
};

/**
 * Check if cached user info is still valid
 */
const isCacheValid = (userAlias: string, tokenHash: string): boolean => {
  if (!userInfoCache) return false;

  const now = Date.now();
  const isExpired = now - userInfoCache.timestamp > CACHE_DURATION;

  return !isExpired && userInfoCache.userAlias === userAlias && userInfoCache.tokenHash === tokenHash;
};

/**
 * Clear the user info cache (useful for testing or when user changes)
 */
export const clearUserInfoCache = (): void => {
  userInfoCache = null;
};

/**
 * Get user access flags from LoggedInUserInfo
 * Now simplified to only return isInitiallyDeveloper flag
 * All permission checks should use Redux userPermission as single source of truth
 *
 * @param user - Logged in user info (can be null/undefined during auth)
 * @returns User access flags object
 */
export const getUserAccessFlags = (user: LoggedInUserInfo | null | undefined): UserAccessFlags => {
  return {
    isInitiallyDeveloper: user?.isInitiallyDeveloper ?? false,
  };
};

/**
 * Get the JWT token from the current session
 * @returns Promise resolving to the JWT token string
 */
export const getToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();

    // Check if we have tokens in the session
    if (!session.tokens?.idToken) {
      throw new Error('No ID token available in the session');
    }

    // Return the ID token as a string
    return session.tokens.idToken.toString();
  } catch (error) {
    console.error('Error getting token:', error);
    throw error;
  }
};

/**
 * Get the Cognito domain for the current user pool
 * @returns The Cognito domain string
 */
export const getCognitoDomain = (): string => {
  const { Region, CognitoUserPoolId } = ENVIRONMENT_VARIABLES.env;
  return `cognito-idp.${Region}.amazonaws.com/${CognitoUserPoolId}`;
};

/**
 * Get AWS service credentials using Cognito Identity Pool
 * @returns Promise resolving to AWS credentials provider
 */
export const getAWSServiceCredentials = async (): Promise<AwsCredentialIdentityProvider> => {
  try {
    // Get the token for authentication
    const token = await getToken();

    // Set up the login map for Cognito Identity Pool
    const COGNITO_LOGIN: Record<string, string> = {};
    COGNITO_LOGIN[getCognitoDomain()] = token;

    const { Region, CognitoIdentityPoolId } = ENVIRONMENT_VARIABLES.env;

    // Create and return the credentials provider
    return fromCognitoIdentityPool({
      clientConfig: { region: Region },
      identityPoolId: CognitoIdentityPoolId,
      logins: COGNITO_LOGIN,
    });
  } catch (error) {
    console.error('Error getting AWS service credentials:', error);
    throw error;
  }
};

/**
 * Extract user information from Cognito user attributes
 * Note: isDeveloper and isReadOnlyUser are now determined by API calls in AuthContext
 * Uses caching to avoid repeated token parsing during authentication flows
 */
export const extractUserInfo = async (authUser: AuthUser): Promise<LoggedInUserInfo> => {
  try {
    // Get the current session to access token details
    const session = await fetchAuthSession();

    // Extract attributes from the ID token payload and safely cast types
    const payload = session.tokens?.idToken?.payload ?? {};

    // Generate user alias first to check cache
    const identities = payload.identities as
      | Array<{ userId?: string; providerName?: string; providerType?: string; primary?: string }>
      | undefined;
    const userAlias =
      identities && identities.length > 0 && typeof identities[0].userId === 'string'
        ? identities[0].userId
        : (authUser.username?.replace('AmazonFederate_', '') ?? '');

    // Generate token hash to detect changes
    const tokenHash = generateTokenHash(payload);

    // Check if we have valid cached data
    if (isCacheValid(userAlias, tokenHash)) {
      return userInfoCache!.userInfo;
    }

    const userInfo: LoggedInUserInfo = {
      userAlias,
      displayName:
        typeof payload['custom:DISPLAY_NAME'] === 'string'
          ? payload['custom:DISPLAY_NAME']
          : typeof payload.name === 'string'
            ? payload.name
            : '',
      givenName:
        typeof payload['custom:GIVEN_NAME'] === 'string'
          ? payload['custom:GIVEN_NAME']
          : typeof payload.given_name === 'string'
            ? payload.given_name
            : '',
      familyName:
        typeof payload['custom:FAMILY_NAME'] === 'string'
          ? payload['custom:FAMILY_NAME']
          : typeof payload.family_name === 'string'
            ? payload.family_name
            : '',
      employeeId: typeof payload['custom:EMPLOYEE_ID'] === 'string' ? payload['custom:EMPLOYEE_ID'] : '',
      departmentName: typeof payload['custom:AMZN_DEPT_NAME'] === 'string' ? payload['custom:AMZN_DEPT_NAME'] : '',
      jobCode: typeof payload['custom:AMZN_JOB_CODE'] === 'string' ? payload['custom:AMZN_JOB_CODE'] : '',
      description: typeof payload['custom:DESCRIPTION'] === 'string' ? payload['custom:DESCRIPTION'] : '',
      // These will be updated by API calls in AuthContext after authentication
      isInitiallyDeveloper: false,
    };

    // Cache the processed user info
    userInfoCache = {
      userAlias,
      tokenHash,
      userInfo,
      timestamp: Date.now(),
    };

    return userInfo;
  } catch (error) {
    console.error('Error extracting user info:', error);
    // Clear cache on error to avoid stale data
    clearUserInfoCache();

    // Return minimal user info in case of error
    return {
      userAlias: authUser.username?.replace('AmazonFederate_', '') ?? '',
      displayName: '',
      givenName: '',
      familyName: '',
      employeeId: '',
      departmentName: '',
      jobCode: '',
      description: '',
      isInitiallyDeveloper: false,
    };
  }
};
